import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { BannerTab, TestimonialsTab } from "./BannerTestimonialsTabs";
import { ReviewsTab, VipCodesTab, ReferralsTab, ClickAnalyticsCard } from "./Phase4Tabs";

const TABS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "rates", label: "Rates & Menu" },
  { id: "about", label: "About / Bio" },
  { id: "faq", label: "FAQ" },
  { id: "banner", label: "Banner" },
  { id: "reviews", label: "Reviews" },
  { id: "vip", label: "VIP Codes" },
  { id: "referrals", label: "Referrals" },
  { id: "gallery", label: "Gallery" },
  { id: "chats", label: "Chat Logs" },
];

// -------- shared bits --------
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[color:var(--muted)]">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function Card({ title, action, children, testId }) {
  return (
    <div className="card-luxe p-5 md:p-6" data-testid={testId}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-4">
          {title && <h3 className="font-serif text-2xl text-gold">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

// -------- Dashboard --------
function Dashboard({ settings, saveSettings, chats }) {
  const [analytics, setAnalytics] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api.adminAnalyticsCtr(days).then(setAnalytics).catch(() => setAnalytics(null));
  }, [days]);

  const set = async (k, v) => {
    await saveSettings({ [k]: v });
    toast.success("Saved");
  };
  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Available Now" testId="admin-toggle-available">
          <p className="text-ivory-dim text-sm mb-4">
            Shows the green pulse on the hero. Toggle off when you're not
            available.
          </p>
          <div className="flex items-center gap-3">
            <button
              className={settings.available_now ? "btn-gold" : "btn-ghost-gold"}
              onClick={() => set("available_now", true)}
              data-testid="admin-available-on"
            >
              Available
            </button>
            <button
              className={!settings.available_now ? "btn-wine" : "btn-ghost-gold"}
              onClick={() => set("available_now", false)}
              data-testid="admin-available-off"
            >
              Away
            </button>
          </div>
        </Card>
        <Card title="After-hours OF mode" testId="admin-toggle-afterhours">
          <p className="text-ivory-dim text-sm mb-4">
            Between 1–10 AM Sydney time, primary CTAs swap to OnlyFans. Add{" "}
            <code className="text-gold">?afterhours=1</code> to any URL to preview.
          </p>
          <div className="flex items-center gap-3">
            <button
              className={settings.after_hours_mode ? "btn-gold" : "btn-ghost-gold"}
              onClick={() => set("after_hours_mode", true)}
              data-testid="admin-afterhours-on"
            >
              On
            </button>
            <button
              className={!settings.after_hours_mode ? "btn-wine" : "btn-ghost-gold"}
              onClick={() => set("after_hours_mode", false)}
              data-testid="admin-afterhours-off"
            >
              Off
            </button>
          </div>
        </Card>
      </div>

      <Card
        title="AI → WhatsApp conversion"
        testId="admin-wa-ctr-card"
        action={
          <div className="flex gap-1">
            {[7, 30, 3650].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-2.5 py-1 text-[0.7rem] tracking-wider uppercase rounded-full border ${
                  days === d
                    ? "border-gold text-gold"
                    : "border-transparent text-ivory-dim hover:text-gold-2"
                }`}
                data-testid={`admin-ctr-range-${d}`}
              >
                {d === 3650 ? "All" : `${d}d`}
              </button>
            ))}
          </div>
        }
      >
        <div className="grid sm:grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--muted)]">
              CTR
            </div>
            <div
              className="font-serif text-5xl text-gold mt-1"
              data-testid="admin-ctr-percent"
            >
              {analytics ? `${analytics.ctr_percent}%` : "—"}
            </div>
            <div className="text-[11px] text-[color:var(--muted)] mt-1">
              {analytics
                ? `${analytics.sessions_with_wa} of ${analytics.sessions} sessions included a WhatsApp handoff`
                : "loading…"}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--muted)]">
              Chats this week
            </div>
            <div className="font-serif text-5xl text-gold mt-1">
              {analytics ? analytics.week_count : "—"}
            </div>
          </div>
          <div className="text-left">
            <div className="text-xs uppercase tracking-widest text-[color:var(--muted)] mb-2 text-center">
              Top questions
            </div>
            <ul className="space-y-1 text-sm text-ivory-dim">
              {(analytics?.top_questions || []).map((q, i) => (
                <li key={i} className="truncate">
                  <span className="text-gold mr-1">·</span>
                  {q.text}{" "}
                  <span className="text-[color:var(--muted)]">
                    ({q.count})
                  </span>
                </li>
              ))}
              {(!analytics || analytics.top_questions.length === 0) && (
                <li className="text-[color:var(--muted)] italic text-xs">
                  Not enough chat data yet.
                </li>
              )}
            </ul>
          </div>
        </div>
      </Card>

      <ClickAnalyticsCard />

      <Card title="Chat stats" testId="admin-chat-stats">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--muted)]">
              Today
            </div>
            <div className="font-serif text-4xl text-gold mt-1">
              {chats?.stats?.today ?? "—"}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--muted)]">
              Total
            </div>
            <div className="font-serif text-4xl text-gold mt-1">
              {chats?.stats?.total ?? "—"}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--muted)]">
              Last message
            </div>
            <div className="font-serif text-sm text-gold-2 mt-3">
              {chats?.stats?.last_at
                ? new Date(chats.stats.last_at).toLocaleString()
                : "—"}
            </div>
          </div>
        </div>
      </Card>

      <Card title="Recent chats">
        <ul className="divide-y divide-[rgba(201,169,97,0.15)]">
          {(chats?.sessions || []).slice(0, 5).map((s) => (
            <li key={s.session_id} className="py-2 flex justify-between gap-3 text-sm">
              <span className="text-ivory truncate">{s.session_id}</span>
              <span className="text-ivory-dim">
                {new Date(s.updated_at).toLocaleString()}
              </span>
            </li>
          ))}
          {(!chats || chats.sessions?.length === 0) && (
            <li className="py-6 text-center text-ivory-dim text-sm">No chats yet.</li>
          )}
        </ul>
      </Card>
    </div>
  );
}

// -------- Rates --------
function RatesEditor({ content, saveContent }) {
  const [rates, setRates] = useState(content.rates || []);
  const [included, setIncluded] = useState(content.included || []);
  const [extras, setExtras] = useState(content.extras || []);
  const [notOffered, setNotOffered] = useState(content.not_offered || []);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRates(content.rates || []);
    setIncluded(content.included || []);
    setExtras(content.extras || []);
    setNotOffered(content.not_offered || []);
  }, [content]);

  const save = async () => {
    setBusy(true);
    try {
      await saveContent({
        rates,
        included,
        extras,
        not_offered: notOffered,
      });
      toast.success("Rates saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const updRate = (i, k, v) => {
    const cp = [...rates];
    cp[i] = { ...cp[i], [k]: v };
    setRates(cp);
  };

  return (
    <div className="space-y-6">
      <Card
        title="Rate rows"
        testId="admin-rates-card"
        action={
          <button
            onClick={() =>
              setRates([...rates, { duration: "", incall: "", outcall: "" }])
            }
            className="btn-ghost-gold !text-[0.7rem]"
            data-testid="admin-add-rate"
          >
            + Add
          </button>
        }
      >
        <div className="space-y-2">
          {rates.map((r, i) => (
            <div
              key={i}
              className="grid grid-cols-[1.4fr_1fr_1fr_auto] gap-2 items-center"
              data-testid={`admin-rate-${i}`}
            >
              <input
                className="input-luxe !py-2"
                value={r.duration}
                onChange={(e) => updRate(i, "duration", e.target.value)}
                placeholder="Duration"
              />
              <input
                className="input-luxe !py-2"
                value={r.incall}
                onChange={(e) => updRate(i, "incall", e.target.value)}
                placeholder="Incall"
              />
              <input
                className="input-luxe !py-2"
                value={r.outcall}
                onChange={(e) => updRate(i, "outcall", e.target.value)}
                placeholder="Outcall"
              />
              <button
                className="btn-ghost-gold !py-1 !px-3 !text-xs"
                onClick={() => setRates(rates.filter((_, j) => j !== i))}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      </Card>

      <StringList
        title="Included in every booking"
        testId="admin-included"
        items={included}
        setItems={setIncluded}
      />
      <ExtrasList extras={extras} setExtras={setExtras} />
      <StringList
        title="Not offered"
        testId="admin-not-offered"
        items={notOffered}
        setItems={setNotOffered}
      />

      <div className="sticky bottom-4 flex justify-end">
        <button
          onClick={save}
          className="btn-gold"
          disabled={busy}
          data-testid="admin-save-rates"
        >
          {busy ? "..." : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function StringList({ title, items, setItems, testId }) {
  return (
    <Card
      title={title}
      testId={testId}
      action={
        <button
          onClick={() => setItems([...(items || []), ""])}
          className="btn-ghost-gold !text-[0.7rem]"
        >
          + Add
        </button>
      }
    >
      <div className="space-y-2">
        {(items || []).map((it, i) => (
          <div key={i} className="flex gap-2">
            <input
              className="input-luxe !py-2"
              value={it}
              onChange={(e) => {
                const cp = [...items];
                cp[i] = e.target.value;
                setItems(cp);
              }}
            />
            <button
              className="btn-ghost-gold !py-1 !px-3 !text-xs"
              onClick={() => setItems(items.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ExtrasList({ extras, setExtras }) {
  return (
    <Card
      title="Extras"
      testId="admin-extras"
      action={
        <button
          onClick={() => setExtras([...(extras || []), { name: "", price: "" }])}
          className="btn-ghost-gold !text-[0.7rem]"
        >
          + Add
        </button>
      }
    >
      <div className="space-y-2">
        {(extras || []).map((e, i) => (
          <div key={i} className="grid grid-cols-[1fr_120px_auto] gap-2">
            <input
              className="input-luxe !py-2"
              value={e.name}
              onChange={(ev) => {
                const cp = [...extras];
                cp[i] = { ...cp[i], name: ev.target.value };
                setExtras(cp);
              }}
              placeholder="Name"
            />
            <input
              className="input-luxe !py-2"
              value={e.price}
              onChange={(ev) => {
                const cp = [...extras];
                cp[i] = { ...cp[i], price: ev.target.value };
                setExtras(cp);
              }}
              placeholder="Price"
            />
            <button
              className="btn-ghost-gold !py-1 !px-3 !text-xs"
              onClick={() => setExtras(extras.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

// -------- About --------
function AboutEditor({ content, saveContent }) {
  const [state, setState] = useState({
    tagline: content.tagline || "",
    hero_blurb: content.hero_blurb || "",
    about_me: content.about_me || [],
    personality_tags: content.personality_tags || [],
    stats: content.stats || [],
    seo_title: content.seo_title || "",
    seo_description: content.seo_description || "",
  });
  useEffect(() => {
    setState({
      tagline: content.tagline || "",
      hero_blurb: content.hero_blurb || "",
      about_me: content.about_me || [],
      personality_tags: content.personality_tags || [],
      stats: content.stats || [],
      seo_title: content.seo_title || "",
      seo_description: content.seo_description || "",
    });
  }, [content]);

  const save = async () => {
    try {
      await saveContent(state);
      toast.success("About saved");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-4">
      <Card title="Copy" testId="admin-about-copy">
        <Field label="Tagline">
          <input
            className="input-luxe"
            value={state.tagline}
            onChange={(e) => setState({ ...state, tagline: e.target.value })}
            data-testid="admin-tagline-input"
          />
        </Field>
        <div className="mt-4" />
        <Field label="Hero blurb">
          <textarea
            className="input-luxe resize-y"
            rows={3}
            value={state.hero_blurb}
            onChange={(e) => setState({ ...state, hero_blurb: e.target.value })}
          />
        </Field>
        <div className="mt-4" />
        <Field label="About me (paragraphs)">
          <div className="space-y-2">
            {state.about_me.map((p, i) => (
              <textarea
                key={i}
                className="input-luxe resize-y"
                rows={3}
                value={p}
                onChange={(e) => {
                  const cp = [...state.about_me];
                  cp[i] = e.target.value;
                  setState({ ...state, about_me: cp });
                }}
              />
            ))}
            <button
              onClick={() => setState({ ...state, about_me: [...state.about_me, ""] })}
              className="btn-ghost-gold !text-[0.7rem]"
            >
              + Add paragraph
            </button>
          </div>
        </Field>
      </Card>
      <StringList
        title="Personality tags"
        items={state.personality_tags}
        setItems={(v) => setState({ ...state, personality_tags: v })}
        testId="admin-personality"
      />
      <Card title="Stats">
        <div className="space-y-2">
          {state.stats.map((s, i) => (
            <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2">
              <input
                className="input-luxe !py-2"
                value={s.label}
                onChange={(e) => {
                  const cp = [...state.stats];
                  cp[i] = { ...cp[i], label: e.target.value };
                  setState({ ...state, stats: cp });
                }}
                placeholder="Label"
              />
              <input
                className="input-luxe !py-2"
                value={s.value}
                onChange={(e) => {
                  const cp = [...state.stats];
                  cp[i] = { ...cp[i], value: e.target.value };
                  setState({ ...state, stats: cp });
                }}
                placeholder="Value"
              />
              <button
                className="btn-ghost-gold !py-1 !px-3 !text-xs"
                onClick={() =>
                  setState({
                    ...state,
                    stats: state.stats.filter((_, j) => j !== i),
                  })
                }
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      </Card>
      <Card title="SEO / Sharing" testId="admin-seo-card">
        <Field label="SEO title (browser tab & social share)">
          <input
            className="input-luxe"
            value={state.seo_title}
            onChange={(e) => setState({ ...state, seo_title: e.target.value })}
            data-testid="admin-seo-title"
            maxLength={120}
          />
        </Field>
        <div className="mt-4" />
        <Field label="SEO description (search + social preview)">
          <textarea
            className="input-luxe resize-y"
            rows={3}
            value={state.seo_description}
            onChange={(e) =>
              setState({ ...state, seo_description: e.target.value })
            }
            maxLength={300}
            data-testid="admin-seo-description"
          />
        </Field>
      </Card>
      <div className="flex justify-end">
        <button
          onClick={save}
          className="btn-gold"
          data-testid="admin-save-about"
        >
          Save
        </button>
      </div>
    </div>
  );
}

// -------- FAQ --------
function FaqEditor({ content, saveContent }) {
  const [faqs, setFaqs] = useState(content.faqs || []);
  useEffect(() => setFaqs(content.faqs || []), [content]);

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= faqs.length) return;
    const cp = [...faqs];
    [cp[i], cp[j]] = [cp[j], cp[i]];
    setFaqs(cp);
  };
  const save = async () => {
    try {
      await saveContent({ faqs });
      toast.success("FAQ saved");
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <div className="space-y-4">
      {faqs.map((f, i) => (
        <Card key={i} testId={`admin-faq-${i}`}>
          <div className="flex justify-between items-start mb-2 gap-2">
            <span className="text-xs uppercase tracking-widest text-[color:var(--muted)]">
              # {i + 1}
            </span>
            <div className="flex gap-1">
              <button
                className="btn-ghost-gold !py-1 !px-2 !text-xs"
                onClick={() => move(i, -1)}
              >
                ↑
              </button>
              <button
                className="btn-ghost-gold !py-1 !px-2 !text-xs"
                onClick={() => move(i, 1)}
              >
                ↓
              </button>
              <button
                className="btn-ghost-gold !py-1 !px-3 !text-xs"
                onClick={() => setFaqs(faqs.filter((_, j) => j !== i))}
              >
                ✕
              </button>
            </div>
          </div>
          <input
            className="input-luxe !py-2 mb-2"
            value={f.q}
            onChange={(e) => {
              const cp = [...faqs];
              cp[i] = { ...cp[i], q: e.target.value };
              setFaqs(cp);
            }}
            placeholder="Question"
          />
          <textarea
            className="input-luxe resize-y"
            rows={3}
            value={f.a}
            onChange={(e) => {
              const cp = [...faqs];
              cp[i] = { ...cp[i], a: e.target.value };
              setFaqs(cp);
            }}
            placeholder="Answer"
          />
        </Card>
      ))}
      <div className="flex justify-between">
        <button
          onClick={() => setFaqs([...faqs, { q: "", a: "" }])}
          className="btn-ghost-gold"
        >
          + Add FAQ
        </button>
        <button onClick={save} className="btn-gold" data-testid="admin-save-faq">
          Save
        </button>
      </div>
    </div>
  );
}

// -------- Gallery --------
function GalleryEditor({ settings, saveSettings, reloadPublic }) {
  const [uploading, setUploading] = useState({});
  const [vipPw, setVipPw] = useState(settings.vip_password || "");
  useEffect(() => setVipPw(settings.vip_password || ""), [settings]);

  const slots = [
    { id: "hero", label: "Hero" },
    { id: "about", label: "About portrait" },
    ...[1, 2, 3, 4, 5, 6].map((n) => ({ id: `gallery-0${n}`, label: `Public ${n}` })),
    ...[1, 2, 3].map((n) => ({ id: `vip-0${n}`, label: `VIP ${n}` })),
  ];

  const upload = async (slot, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [slot]: true }));
    try {
      await api.adminUploadGallery(slot, file);
      toast.success(`Uploaded ${slot}`);
      reloadPublic?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading((u) => ({ ...u, [slot]: false }));
    }
  };

  const saveVip = async () => {
    try {
      await saveSettings({ vip_password: vipPw });
      toast.success("VIP password updated");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <Card title="Image slots" testId="admin-gallery">
        <div className="grid sm:grid-cols-2 gap-4">
          {slots.map((s) => (
            <div key={s.id} className="flex items-center gap-3">
              <div className="wm-wrap w-16 h-16 rounded-lg overflow-hidden border border-[rgba(201,169,97,0.25)] flex-shrink-0">
                <img
                  src={`/gallery/${s.id}.jpg?v=${Date.now()}`}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-ivory text-sm">{s.label}</div>
                <div className="text-xs text-[color:var(--muted)] truncate">
                  {s.id}.jpg
                </div>
              </div>
              <label className="btn-ghost-gold !py-1.5 !px-3 !text-[0.7rem] cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => upload(s.id, e.target.files?.[0])}
                  data-testid={`admin-upload-${s.id}`}
                />
                {uploading[s.id] ? "..." : "Replace"}
              </label>
            </div>
          ))}
        </div>
      </Card>

      <Card title="VIP gallery password" testId="admin-vip-password-card">
        <Field label="Current password">
          <div className="flex gap-2">
            <input
              className="input-luxe"
              value={vipPw}
              onChange={(e) => setVipPw(e.target.value)}
              data-testid="admin-vip-password-input"
            />
            <button
              onClick={saveVip}
              className="btn-gold"
              data-testid="admin-save-vip-password"
            >
              Update
            </button>
          </div>
        </Field>
      </Card>
    </div>
  );
}

// -------- Chat logs --------
function ChatsView({ chats, reload }) {
  const [selected, setSelected] = useState(null);

  const openSession = async (id) => {
    try {
      const doc = await api.adminChat(id);
      setSelected(doc);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this session?")) return;
    try {
      await api.adminDeleteChat(id);
      toast.success("Deleted");
      setSelected(null);
      reload();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-4">
      <Card testId="admin-chats-list">
        <div className="max-h-[70vh] overflow-y-auto divide-y divide-[rgba(201,169,97,0.15)]">
          {(chats?.sessions || []).map((s) => (
            <button
              key={s.session_id}
              onClick={() => openSession(s.session_id)}
              className={`block w-full text-left py-2.5 px-1 hover:bg-[rgba(201,169,97,0.05)] transition-colors ${
                selected?.session_id === s.session_id ? "text-gold" : "text-ivory"
              }`}
              data-testid={`admin-chat-row-${s.session_id}`}
            >
              <div className="text-sm truncate">{s.session_id}</div>
              <div className="text-xs text-[color:var(--muted)]">
                {new Date(s.updated_at).toLocaleString()}
              </div>
            </button>
          ))}
          {(chats?.sessions || []).length === 0 && (
            <div className="text-center text-ivory-dim py-6 text-sm">No chats yet.</div>
          )}
        </div>
      </Card>
      <Card
        title={selected ? selected.session_id : "Select a chat"}
        action={
          selected && (
            <button
              onClick={() => del(selected.session_id)}
              className="btn-ghost-gold !text-[0.7rem]"
            >
              Delete
            </button>
          )
        }
      >
        {selected ? (
          <div className="space-y-3 max-h-[65vh] overflow-y-auto">
            {selected.messages?.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                    m.role === "user"
                      ? "bg-[#3a1219] text-ivory"
                      : "bg-[#2a2418] text-gold-2"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-ivory-dim text-sm">
            Pick a session on the left to see the full transcript.
          </p>
        )}
      </Card>
    </div>
  );
}

// -------- Root Admin App --------
export default function AdminApp({ onSiteReload }) {
  const [tab, setTab] = useState("dashboard");
  const [content, setContent] = useState(null);
  const [settings, setSettings] = useState(null);
  const [chats, setChats] = useState(null);
  const nav = useNavigate();

  const loadContent = async () => setContent(await api.adminGetContent());
  const loadSettings = async () => setSettings(await api.adminGetSettings());
  const loadChats = async () => setChats(await api.adminChats());

  useEffect(() => {
    loadContent();
    loadSettings();
    loadChats();
  }, []);

  const saveContent = async (updates) => {
    const next = await api.adminSaveContent(updates);
    setContent(next);
    onSiteReload?.();
  };
  const saveSettings = async (updates) => {
    const next = await api.adminSaveSettings(updates);
    setSettings(next);
    onSiteReload?.();
  };

  const logout = async () => {
    try {
      await api.adminLogout();
    } catch {}
    nav("/admin/login");
    window.location.reload();
  };

  if (!content || !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center text-ivory-dim">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="border-b border-[rgba(201,169,97,0.2)] px-5 md:px-8 py-4 flex items-center justify-between sticky top-0 bg-[rgba(10,10,10,0.9)] backdrop-blur z-20">
        <div className="font-display text-2xl text-gold">Camilla XXX · Admin</div>
        <div className="flex items-center gap-3">
          <a href="/" className="nav-link" data-testid="admin-view-site">
            View site
          </a>
          <button onClick={logout} className="btn-ghost-gold !text-[0.7rem]" data-testid="admin-logout">
            Sign out
          </button>
        </div>
      </header>

      <nav className="border-b border-[rgba(201,169,97,0.15)] px-5 md:px-8 overflow-x-auto">
        <div className="container-x flex gap-1 md:gap-3">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`py-3 px-3 md:px-4 text-[0.72rem] uppercase tracking-[0.16em] transition-colors border-b-2 ${
                tab === t.id
                  ? "text-gold border-gold"
                  : "text-ivory-dim border-transparent hover:text-gold-2"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="container-x px-5 md:px-8 py-6 md:py-10 max-w-5xl">
        {tab === "dashboard" && (
          <Dashboard settings={settings} saveSettings={saveSettings} chats={chats} />
        )}
        {tab === "rates" && <RatesEditor content={content} saveContent={saveContent} />}
        {tab === "about" && <AboutEditor content={content} saveContent={saveContent} />}
        {tab === "faq" && <FaqEditor content={content} saveContent={saveContent} />}
        {tab === "banner" && (
          <BannerTab settings={settings} saveSettings={saveSettings} />
        )}
        {tab === "reviews" && (
          <ReviewsTab
            settings={settings}
            saveSettings={saveSettings}
            onSiteReload={onSiteReload}
            reloadSettings={loadSettings}
          />
        )}
        {tab === "vip" && <VipCodesTab />}
        {tab === "referrals" && <ReferralsTab />}
        {tab === "gallery" && (
          <GalleryEditor
            settings={settings}
            saveSettings={saveSettings}
            reloadPublic={onSiteReload}
          />
        )}
        {tab === "chats" && <ChatsView chats={chats} reload={loadChats} />}
      </main>
    </div>
  );
}
